import React, { useState, useRef, useEffect } from 'react';
import { Table, TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import './ResizableTable.css';

interface ResizableTableProps<T> extends Omit<TableProps<T>, 'columns'> {
  columns: ColumnsType<T>;
}

const ResizableTable = <T extends object>({ columns, ...tableProps }: ResizableTableProps<T>) => {
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  // 初始化列宽
  useEffect(() => {
    const initialWidths: { [key: string]: number } = {};
    columns.forEach((col: any) => {
      if (col.width) {
        initialWidths[col.key || col.dataIndex] = col.width;
      }
    });
    setColumnWidths(initialWidths);
  }, [columns]);

  // 处理拖拽开始
  const handleMouseDown = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnKey);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 处理拖拽移动
  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing || !resizingColumn || !tableRef.current) return;

    const tableRect = tableRef.current.getBoundingClientRect();
    const newWidth = e.clientX - tableRect.left;
    
    if (newWidth > 50) { // 最小宽度限制
      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn]: newWidth
      }));
    }
  };

  // 处理拖拽结束
  const handleMouseUp = () => {
    setIsResizing(false);
    setResizingColumn(null);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // 创建可拖拽的列
  const resizableColumns = columns.map((col: any) => {
    const columnKey = col.key || col.dataIndex;
    const currentWidth = columnWidths[columnKey] || col.width || 100;

    return {
      ...col,
      width: currentWidth,
      title: (
        <div className="resizable-column-header">
          <span>{col.title}</span>
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, columnKey)}
          />
        </div>
      ),
    };
  });

  return (
    <div 
      ref={tableRef} 
      className={`resizable-table ${isResizing ? 'resizing' : ''}`}
    >
      <Table
        {...tableProps}
        columns={resizableColumns}
        className="resizable-table-content"
        scroll={{ x: 1200, y: 500 }}
      />
    </div>
  );
};

export default ResizableTable; 